import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Input } from 'antd';
import { StopOutlined, WarningFilled } from '@ant-design/icons';
import { ISubscriptionItem } from '../../../../pages/subscriptions/controllers/useSubscriptionsPageController';
import CompanyContext from '../../../../contexts/company/CompanyContext';
import { EBillingInterval } from '@leedpulse-monorepo/enums';
import { cancellationDateFor } from '@leedpulse-monorepo/utils';
import './CancelNumberModal.css';
import dayjs from 'dayjs';

interface CancelNumberModalProps {
  isOpen: boolean;
  subscription: ISubscriptionItem | null;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (subscription: ISubscriptionItem) => void;
}

const CancelNumberModal: React.FC<CancelNumberModalProps> = ({
  isOpen,
  subscription,
  isLoading = false,
  onCancel,
  onConfirm,
}) => {
  const { activeCompany } = useContext(CompanyContext);
  const [confirmationInput, setConfirmationInput] = useState('');

  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) setConfirmationInput('');
  }

  if (!subscription) return null;

  // Format phone for confirmation (remove spaces)
  const phoneForConfirmation = subscription.sender.replace(/\s/g, '');
  const isConfirmEnabled = confirmationInput === phoneForConfirmation;

  // Cancellation takes effect at the end of the paid period, which is exactly what Stripe
  // holds. `plan_end - 1 month` is the fallback for a company whose billing mirror is missing:
  // plan_end carries a month of grace on top of the period.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const planEnd: number | undefined = (subscription as any).plan_end;
  const periodEnd = activeCompany?.billing?.period_end
    ?? (planEnd ? dayjs(planEnd).subtract(1, 'month').valueOf() : undefined);

  // An annual subscription commits for the year: the period end IS the end of the commitment,
  // so cancelling can never cut it short.
  const isAnnual = activeCompany?.billing?.interval === EBillingInterval.YEAR;

  // An engaged number is paid until its engagement runs out: the cut then lands on the first
  // period ending after it. Same function as the server, so the two cannot disagree.
  const effectiveEnd = periodEnd
    ? cancellationDateFor(periodEnd, isAnnual ? 12 : 1, subscription.commitment_date ?? null)
    : undefined;
  const isDeferredByCommitment = !!effectiveEnd && !!periodEnd && effectiveEnd > periodEnd;

  const formattedDate = effectiveEnd ? dayjs(effectiveEnd).locale('fr').format('DD MMMM YYYY') : '—';

  const handleConfirm = () => {
    onConfirm(subscription);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      centered
      width={612}
      className="cancel-number-modal"
      closable={false}
    >
      <div className="cancel-number-modal__content">
        {/* Header */}
        <div className="cancel-number-modal__header">
          <StopOutlined className="cancel-number-modal__icon" />
          <h3 className="cancel-number-modal__title">Résilier le numéro</h3>
        </div>

        {/* Number info section */}
        <div className="cancel-number-modal__info">
          <div className="cancel-number-modal__info-row">
            <div className="cancel-number-modal__info-left">
              <span className="cancel-number-modal__info-label">Numéro</span>
              <span className="cancel-number-modal__phone">{subscription.sender_formatted}</span>
              {subscription.usernames.length > 0 && (
                <span className="cancel-number-modal__assigned">Assigné à {subscription.usernames.join(', ')}</span>
              )}
            </div>
          </div>
        </div>

        {/* Termination date */}
        <div className="cancel-number-modal__date-section">
          <span className="cancel-number-modal__date-label">
            {isAnnual || isDeferredByCommitment ? "Fin d'engagement et de service :" : 'Date de résiliation effective :'}
          </span>
          <span className="cancel-number-modal__date">{formattedDate}</span>
        </div>

        {/* Warning box */}
        <div className="cancel-number-modal__warning-box">
          <div className="cancel-number-modal__warning-header">
            <WarningFilled className="cancel-number-modal__warning-icon" />
            <span className="cancel-number-modal__warning-title">
              Attention : cette action est irréversible
            </span>
          </div>
          <ul className="cancel-number-modal__warning-list">
            <li>Le numéro restera actif jusqu'au <strong>{formattedDate}</strong>, puis sera définitivement libéré</li>
            {isDeferredByCommitment && (
              <li>Ce numéro est sous engagement : il reste facturé jusqu'au <strong>{formattedDate}</strong></li>
            )}
            <li>L'historique des appels et les transcriptions seront supprimés à la date de résiliation</li>
            {subscription.usernames.length > 0 && <li>{subscription.usernames.join(', ')} perdra l'accès à la date de résiliation</li>}
            <li>Aucun remboursement pour la période en cours</li>
          </ul>
        </div>

        {/* Confirmation input */}
        <div className="cancel-number-modal__confirmation">
          <p className="cancel-number-modal__confirmation-label">
            Entrez le numéro <strong>{phoneForConfirmation}</strong> pour confirmer
          </p>
          <Input
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            placeholder={phoneForConfirmation}
            className="cancel-number-modal__confirmation-input"
          />
        </div>

        {/* Actions */}
        <div className="cancel-number-modal__actions">
          <Button className="cancel-number-modal__cancel-btn" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="primary"
            danger
            className="cancel-number-modal__confirm-btn"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!isConfirmEnabled}
          >
            Résilier
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelNumberModal;
